import { logger } from './logger';
/**
 * Detect when a new frontend build is deployed and reload once to avoid
 * stale-bundle runtime errors (e.g. missing methods on service objects).
 */

import { markReload, wasRecentlyReloaded } from './lazyWithRetry';

interface VersionPayload {
  version?: string;
}

/**
 * Fetch deployed version.json and reload if it differs from the running build.
 * Uses the same reload loop guard as lazyWithRetry.
 */
export async function checkForAppUpdate(): Promise<void> {
  if (import.meta.env.DEV) {
    return;
  }

  try {
    const response = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: 'no-store',
    });

    if (!response.ok) {
      return;
    }

    const data = (await response.json()) as VersionPayload;
    const deployedVersion = data.version;

    if (!deployedVersion || deployedVersion === __APP_VERSION__) {
      return;
    }

    if (wasRecentlyReloaded()) {
      logger.warn('[versionCheck] Build version mismatch persists after reload', {
        running: __APP_VERSION__,
        deployed: deployedVersion,
      });
      return;
    }

    logger.warn('[versionCheck] New build detected — reloading', {
      running: __APP_VERSION__,
      deployed: deployedVersion,
    });
    markReload();
    window.location.reload();
  } catch (error) {
    logger.warn('[versionCheck] Failed to check for app update', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Poll for updates when the tab becomes visible again.
 */
export function startVersionCheck(): void {
  void checkForAppUpdate();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForAppUpdate();
    }
  });
}
